// Built-in preset bundles. Vite inlines these JSON modules at build
// time so they ship in the Designer bundle (small enough not to
// matter — three small surveys + one instruction).
//
// At runtime, controllerd-served bundles (12g) and local-disk
// uploads funnel through the same `mergePresetIntoStudy` function;
// these built-ins are just one more source.

import sus from './sus.preset.json' with { type: 'json' };
import nasaTlx from './nasa-tlx.preset.json' with { type: 'json' };
import nasaTlxWeights from './nasa-tlx-weights.preset.json' with { type: 'json' };
import umuxLite from './umux-lite.preset.json' with { type: 'json' };
import demographics from './demographics.preset.json' with { type: 'json' };

import { validatePresetBundle, type PresetBundle, type StudyPayload } from '../schema.ts';

export interface BuiltInPreset {
  /** Stable id used in the picker UI; never persisted. */
  id: string;
  bundle: PresetBundle;
}

/** All built-in presets that ship in the bundle. */
export const BUILTIN_PRESETS: BuiltInPreset[] = [
  { id: 'sus', bundle: validateOrThrow(sus, 'sus') },
  { id: 'umux-lite', bundle: validateOrThrow(umuxLite, 'umux-lite') },
  { id: 'nasa-tlx', bundle: validateOrThrow(nasaTlx, 'nasa-tlx') },
  { id: 'nasa-tlx-weights', bundle: validateOrThrow(nasaTlxWeights, 'nasa-tlx-weights') },
  { id: 'demographics', bundle: validateOrThrow(demographics, 'demographics') },
];

function validateOrThrow(raw: unknown, id: string): PresetBundle {
  const r = validatePresetBundle(raw);
  if (!r.ok || r.value === null) {
    // A built-in preset failing validation is a build-time bug, not
    // a runtime user error — surface it loudly.
    const messages = r.errors.map((e) => `${e.path}: ${e.message}`).join('; ');
    throw new Error(`built-in preset "${id}" is malformed: ${messages}`);
  }
  return r.value;
}

export type CollisionResolution = 'rename' | 'replace' | 'skip';

export interface ImportSelection {
  /** Pick which library kinds and ids to bring in from the bundle. */
  surveys?: string[];
  instructions?: string[];
  tasks?: string[];
  prompts?: string[];
}

export interface CollisionPlan {
  surveys: Record<string, CollisionResolution>;
  instructions: Record<string, CollisionResolution>;
  tasks: Record<string, CollisionResolution>;
  prompts: Record<string, CollisionResolution>;
}

export function emptyCollisionPlan(): CollisionPlan {
  return { surveys: {}, instructions: {}, tasks: {}, prompts: {} };
}

/** Detect collisions for the selected entries against the study's library. */
export function detectCollisions(
  payload: StudyPayload,
  bundle: PresetBundle,
  selection: ImportSelection,
): { surveys: string[]; instructions: string[]; tasks: string[]; prompts: string[] } {
  return {
    surveys: pickColliding(
      payload.library.surveys,
      bundle.library.surveys,
      selection.surveys,
    ),
    instructions: pickColliding(
      payload.library.instructions,
      bundle.library.instructions,
      selection.instructions,
    ),
    tasks: pickColliding(payload.library.tasks, bundle.library.tasks, selection.tasks),
    prompts: pickColliding(
      payload.library.prompts,
      bundle.library.prompts,
      selection.prompts,
    ),
  };
}

function pickColliding(
  destination: Record<string, unknown>,
  source: Record<string, unknown>,
  selectedIds?: string[],
): string[] {
  const ids = selectedIds ?? Object.keys(source);
  return ids.filter((id) => Object.prototype.hasOwnProperty.call(destination, id));
}

/** Apply the bundle subset, honoring per-id collision resolution. */
export function mergePresetIntoStudy(
  payload: StudyPayload,
  bundle: PresetBundle,
  selection: ImportSelection,
  collisions: CollisionPlan,
): StudyPayload {
  const next: StudyPayload = {
    ...payload,
    library: {
      surveys: { ...payload.library.surveys },
      instructions: { ...payload.library.instructions },
      tasks: { ...payload.library.tasks },
      prompts: { ...payload.library.prompts },
    },
  };
  applyKind(
    next.library.surveys,
    bundle.library.surveys,
    selection.surveys,
    collisions.surveys,
  );
  applyKind(
    next.library.instructions,
    bundle.library.instructions,
    selection.instructions,
    collisions.instructions,
  );
  applyKind(next.library.tasks, bundle.library.tasks, selection.tasks, collisions.tasks);
  applyKind(
    next.library.prompts,
    bundle.library.prompts,
    selection.prompts,
    collisions.prompts,
  );
  return next;
}

function applyKind<T>(
  destination: Record<string, T>,
  source: Record<string, T>,
  selectedIds: string[] | undefined,
  perIdResolution: Record<string, CollisionResolution>,
): void {
  const ids = selectedIds ?? Object.keys(source);
  for (const id of ids) {
    if (!(id in source)) continue;
    if (id in destination) {
      const r = perIdResolution[id] ?? 'rename';
      if (r === 'skip') continue;
      if (r === 'replace') {
        destination[id] = source[id]!;
        continue;
      }
      // rename — append -2, -3, ... until free
      destination[uniqueKey(destination, id)] = source[id]!;
    } else {
      destination[id] = source[id]!;
    }
  }
}

function uniqueKey(map: Record<string, unknown>, base: string): string {
  for (let i = 2; i < 1000; i += 1) {
    const id = `${base}-${i}`;
    if (!(id in map)) return id;
  }
  return `${base}-${Date.now()}`;
}
